/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Solid gold (DECISIONS.md D2): hover and pressed are derived from --primary, ink is --primary-foreground (#1c1606).
const primaryButtonStyles =
  "focus-visible:ring-[hsl(var(--focus-ring-on-accent))] bg-primary text-primary-foreground font-semibold shadow-[0_1px_4px_hsl(var(--primary)/0.2)] hover:bg-primary-hover hover:shadow-[0_2px_8px_hsl(var(--primary)/0.3)] active:brightness-95";

export type CanonicalButtonVariant = "primary" | "outline" | "secondary" | "ghost";
export type ButtonVariantAlias = CanonicalButtonVariant | "default" | "destructive" | "link";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] text-body font-medium ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-[length:var(--focus-ring-width)] focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:translate-y-[1px] active:scale-[0.98] touch-manipulation",
  {
    variants: {
      variant: {
        primary: primaryButtonStyles,
        default: primaryButtonStyles,
        destructive:
          "bg-destructive text-destructive-foreground shadow-[0_1px_2px_rgba(0,0,0,0.1)] hover:bg-destructive/90 hover:-translate-y-[1px] hover:shadow-[0_2px_6px_rgba(0,0,0,0.15)]",
        // An outline Button's border is the control border an Input and a SelectTrigger draw
        // (`border-input`, the shadcn convention), as a bare utility so tailwind-merge drops it when
        // a caller passes its own border colour, e.g. `border-destructive` on an invalid field. A
        // combobox trigger built on it (role="combobox": a field that happens to be a button) then
        // matches the field beside it, and a caller's colour is not beaten by a variant-prefixed rule
        // of higher specificity (#7912). To go back to the faint hairline, put `border-hairline` here.
        outline:
          "border border-input bg-background/70 text-foreground shadow-soft hover:bg-hover hover:text-foreground hover:-translate-y-[1px]",
        secondary: "bg-secondary text-secondary-foreground shadow-soft hover:bg-hover",
        ghost: "text-foreground hover:bg-hover hover:text-foreground",
        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        // DECISIONS.md D1. Heights are tokens: 36px by default, 30px `compact` (tables and toolbars,
        // opt-in), 44px `touch`, and 44px for every size below 1024px and on kiosks (tokens.css).
        // `sm` keeps its old 36px, which is now the default height, with its tighter padding; `lg`
        // keeps its name for callers and is the touch height.
        default: "h-[var(--button-height)] min-h-[var(--button-height)] px-[var(--button-padding-x)]",
        compact: "h-[var(--button-height-compact)] min-h-[var(--button-height-compact)] px-3 text-sm",
        sm: "h-[var(--button-height)] min-h-[var(--button-height)] px-3 text-sm",
        touch: "h-[var(--button-height-touch)] min-h-[var(--button-height-touch)] px-5",
        lg: "h-[var(--button-height-touch)] min-h-[var(--button-height-touch)] px-6 text-base",
        icon: "h-[var(--button-height)] w-[var(--button-height)] min-h-[var(--button-height)] min-w-[var(--button-height)]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export type ButtonVariantProps = ButtonProps["variant"];

const Button = React.memo(
  React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }),
);
Button.displayName = "Button";

export { Button, buttonVariants };